import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  Kit,
  PaginatedResponse,
  CreateKitPayload,
  KitDeployPayload,
  KitReturnPayload,
  KitAssignTechPayload,
  KitInspectPayload,
  KitDeployResponse,
  KitReturnResponse,
  KitAssignTechResponse,
  KitInspectResponse,
  KitStatus,
} from '@/lib/types';

export interface KitFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: KitStatus | '';
  assigned_to_tech?: number | '';
  ordering?: string;
}

export function useKitsForAsset(assetId: number) {
  return useQuery({
    queryKey: [...queryKeys.kits.all, 'for-asset', assetId] as const,
    queryFn: async (): Promise<Kit[]> => {
      const response = await apiClient.get<PaginatedResponse<Kit>>('/api/v1/kits/', {
        params: { page_size: 100 },
      });
      return response.data.results.filter((kit) => kit.assets.includes(assetId));
    },
    enabled: assetId > 0,
  });
}

export function useKits(filters: KitFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.kits.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<Kit>> => {
      const response = await apiClient.get<PaginatedResponse<Kit>>('/api/v1/kits/', {
        params: cleanFilters,
      });
      return response.data;
    },
  });
}

export function useKit(id: number) {
  return useQuery({
    queryKey: queryKeys.kits.detail(id),
    queryFn: async (): Promise<Kit> => {
      const response = await apiClient.get<Kit>(`/api/v1/kits/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateKitPayload): Promise<Kit> => {
      const response = await apiClient.post<Kit>('/api/v1/kits/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
    },
  });
}

export function useUpdateKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateKitPayload> }): Promise<Kit> => {
      const response = await apiClient.patch<Kit>(`/api/v1/kits/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.detail(variables.id) });
    },
  });
}

export function useDeleteKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/kits/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
    },
  });
}

export function useAddAssetsToKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, assetIds }: { id: number; assetIds: number[] }): Promise<Kit> => {
      const response = await apiClient.post<Kit>(`/api/v1/kits/${id}/add_assets/`, {
        asset_ids: assetIds,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useRemoveAssetsFromKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, assetIds }: { id: number; assetIds: number[] }): Promise<Kit> => {
      const response = await apiClient.post<Kit>(`/api/v1/kits/${id}/remove_assets/`, {
        asset_ids: assetIds,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useDeployKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: KitDeployPayload }): Promise<KitDeployResponse> => {
      const response = await apiClient.post<KitDeployResponse>(`/api/v1/kits/${id}/deploy/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
    },
  });
}

export function useReturnKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data?: KitReturnPayload }): Promise<KitReturnResponse> => {
      const response = await apiClient.post<KitReturnResponse>(`/api/v1/kits/${id}/return/`, data || {});
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
    },
  });
}

export function useAssignTechToKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: KitAssignTechPayload }): Promise<KitAssignTechResponse> => {
      const response = await apiClient.post<KitAssignTechResponse>(`/api/v1/kits/${id}/assign_tech/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.detail(variables.id) });
    },
  });
}

export function useUnassignTechFromKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<KitAssignTechResponse> => {
      const response = await apiClient.post<KitAssignTechResponse>(`/api/v1/kits/${id}/unassign_tech/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.detail(id) });
    },
  });
}

export function useInspectKit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: KitInspectPayload }): Promise<KitInspectResponse> => {
      const response = await apiClient.post<KitInspectResponse>(`/api/v1/kits/${id}/inspect/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.kits.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}
